// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useMemo, useState } from 'react';
import { Box, Slider, Switch, FormControlLabel, Typography } from '@mui/material';
import { ReTensionCard, ReTensionSpan } from './chatbotTypes';

// The re_tension result rendered as an interactive what-if: a tension slider + a storm toggle that recompute
// each span's midspan clearance LIVE off the Lambda's measured anchors, reporting how many drop below the
// NESC minimum. Sag ∝ 1/tension at constant weight (sound); the storm case scales sag by the ice-loaded
// weight ratio (a conservative upper bound the Lambda supplies). Local-only state; no Redux, no viewer refs.

const mutedSx = { fontSize: '0.72rem', color: 'grey.400' } as const;
const textSx = { fontSize: '0.72rem', color: '#eee' } as const;
const PASS_GREEN = '#37C871';
const FAIL_RED = '#FF6B6B';

// A span's midspan clearance at tension multiplier `m` (parabolic sag scales as 1/m) with an optional storm
// sag factor, anchored on the measured as-built (m = 1, no storm → clearance is exactly the as-built value).
// Exported so a test can pin parity with the Lambda's storm formula (clearance − sag·(ratio − 1) at m = 1).
export const clearanceAt = (span: ReTensionSpan, multiplier: number, stormFactor: number): number => {
  const sag = (span.sag_ft * stormFactor) / multiplier;
  return span.clearance_ft - (sag - span.sag_ft);
};

const ReTensionSlider: React.FC<{ card: ReTensionCard }> = ({ card }) => {
  const spans = card._spans ?? [];
  const [multiplier, setMultiplier] = useState(1);
  // Open in the STORM case: the Lambda highlights the storm-failing spans red on the map and leads the prose
  // with storm_fail_count, so the card's headline must match that framing on first render (toggle off for the
  // bare/as-built case). At 1x + storm the live count equals the Lambda's storm_fail_count exactly.
  const [storm, setStorm] = useState(true);
  const min = card.min_clearance_ft;

  const { failing, worst } = useMemo(() => {
    const stormFactor = storm ? card.storm_sag_ratio : 1;
    let fails = 0;
    let lowest = Infinity;
    spans.forEach((span) => {
      const clearance = clearanceAt(span, multiplier, stormFactor);
      if (clearance < min) fails += 1;
      if (clearance < lowest) lowest = clearance;
    });
    return { failing: fails, worst: Number.isFinite(lowest) ? lowest : null };
  }, [spans, multiplier, storm, card.storm_sag_ratio, min]);

  if (!spans.length) {
    return <Typography sx={mutedSx}>No spans with an estimable sag to screen.</Typography>;
  }

  const failColor = failing > 0 ? FAIL_RED : PASS_GREEN;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography sx={mutedSx}>
        {`${card.span_count} spans · ${card.district} district · storm ×${card.storm_sag_ratio.toFixed(2)}`}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ ...textSx, minWidth: 92 }}>{`Tension ${multiplier.toFixed(2)}×`}</Typography>
        <Slider
          size="small"
          value={multiplier}
          min={0.5}
          max={2}
          step={0.05}
          onChange={(_e, value) => setMultiplier(Array.isArray(value) ? value[0] : value)}
          sx={{ color: '#01A1DD', flex: 1 }}
          aria-label="Tension multiplier"
        />
      </Box>

      <FormControlLabel
        control={<Switch size="small" checked={storm} onChange={(e) => setStorm(e.target.checked)} />}
        label={<Typography sx={textSx}>{`Storm loading (${card.district})`}</Typography>}
        sx={{ ml: 0 }}
      />

      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: failColor }}>
        {`${failing} of ${spans.length} spans below ${min.toFixed(0)} ft`}
      </Typography>
      {worst !== null && <Typography sx={mutedSx}>{`Worst clearance: ${worst.toFixed(1)} ft`}</Typography>}

      {card.note && <Typography sx={mutedSx}>{card.note}</Typography>}
    </Box>
  );
};

export default ReTensionSlider;
